import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { WHY_CHOOSE_TABS } from "../constants/whyChooseData";

// Tab-based Q&A with Carousel Content
export const WhyChooseSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isPaused || isDragging) return;

    const interval = setInterval(() => {
      setActiveTab((prev) => prev + 1);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [isPaused, isDragging]);

  useEffect(() => {
    // Reset to the actual slide after transition completes
    if (activeTab === 3) {
      setTimeout(() => {
        setIsTransitioning(false);
        setActiveTab(0);
        setTimeout(() => {
          setIsTransitioning(true);
        }, 50);
      }, 500); // Match transition duration
    }
  }, [activeTab]);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    setIsTransitioning(false);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setStartX(clientX);
    setCurrentX(clientX);
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setCurrentX(clientX);
    const diff = clientX - startX;
    setDragOffset(diff);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    setIsTransitioning(true);

    const diff = currentX - startX;
    const threshold = 50; // Minimum drag distance to trigger slide change

    if (diff > threshold) {
      // Dragged right - go to previous slide
      setActiveTab((prev) => Math.max(0, prev - 1));
    } else if (diff < -threshold) {
      // Dragged left - go to next slide
      setActiveTab((prev) => prev + 1);
    }

    setDragOffset(0);
  };

  return (
    <section className="relative py-12 w-screen -mx-[50vw] left-1/2 right-1/2 bg-background">
      <div className="relative w-full px-6 lg:px-8">
        {/* Heading */}
        <div className="mb-8 max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-foreground">
            One managment platform for all
          </h2>
        </div>

        {/* Clickable Text Tabs */}
        <div
          className="mb-10 text-2xl sm:text-3xl lg:text-4xl xl:text-5xl max-w-7xl mx-auto leading-relaxed"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {WHY_CHOOSE_TABS.map((tab, index) => (
            <span
              key={index}
              onClick={() => {
                setIsTransitioning(true);
                setActiveTab(index);
              }}
              className={`cursor-pointer transition-all duration-300 ${
                activeTab % 3 === index
                  ? `tab-active-progress tab-active-progress-${index + 1} ${isPaused ? 'paused' : ''}`
                  : `tab-inactive tab-inactive-${index + 1}`
              }`}
            >
              {tab.phrase}{' '}
            </span>
          ))}
        </div>

        {/* Carousel Content */}
        <div
          className="relative overflow-hidden max-w-7xl mx-auto cursor-grab active:cursor-grabbing"
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          <div
            ref={carouselRef}
            className={`flex ${isTransitioning ? 'transition-transform duration-500 ease-out' : ''}`}
            style={{
              transform: `translateX(calc(-${activeTab * (100 + 10)}% + ${dragOffset}px))`,
              userSelect: isDragging ? 'none' : 'auto'
            }}
          >
            {[...WHY_CHOOSE_TABS, WHY_CHOOSE_TABS[0]].map((tab, index) => (
              <div key={index} className="w-full flex-shrink-0" style={{ marginRight: '10%' }}>
                {/* Feature Cards Carousel */}
                <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-fr gap-6 max-w-7xl mx-auto mb-4">
                  {tab.content.features.map((feature, i) => (
                    <div
                      key={i}
                      className={`group bg-card rounded-xl overflow-hidden border border-border hover:border-${tab.color}/50 transition-all duration-300 hover:shadow-lg ${feature.span} flex flex-col min-h-[32rem] relative`}
                    >
                      <img
                        src={feature.image}
                        alt={feature.label}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      {/* Muting overlay */}
                      <div className="absolute inset-0 bg-black/25 pointer-events-none"></div>
                      {/* Bottom gradient */}
                      <div className="absolute inset-x-0 bottom-0 h-40 z-10 bg-gradient-to-t from-black/80 via-black/50 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-500 ease-in-out pointer-events-none"></div>
                      <div className="relative z-10 mt-auto p-4 group-hover:p-6 transition-all duration-300">
                        <h4 className="text-xl font-semibold text-white mb-1 group-hover:text-3xl transition-all duration-300">
                          {feature.label}
                        </h4>
                        <p className="text-sm text-white/70 group-hover:text-white/90 group-hover:text-lg transition-all duration-300">
                          {feature.detail}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-left max-w-7xl mx-auto">
                  <h3
                    className="text-4xl font-normal tracking-tight"
                    style={{
                      background: index % 3 === 0
                        ? 'linear-gradient(to right, var(--primary-light), var(--grow-hex))'
                        : index % 3 === 1
                        ? 'linear-gradient(to right, var(--grow-hex), var(--primary-light))'
                        : 'linear-gradient(to right, var(--primary-light), var(--secondary-hex))',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      color: 'transparent',
                      display: 'inline-block'
                    }}
                  >
                    {tab.content.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-right -mt-10 max-w-7xl mx-auto flex gap-4 justify-end">
          <Button size="lg" variant="outline" className="px-8" asChild>
            <Link to="/sign-up">
              Get Started Now <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" className="px-8" asChild>
            <Link to="/features">
              Explore All Features
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
